/**
 * O painel de leitura, ao lado da lista.
 *
 * O layout do arquivo único já tinha sido corrigido depois de o usuário
 * reclamar que "não dá pra ler muito bem": a ficha subiu para antes das
 * listas longas, riscos viraram blocos com respiro, listas com mais de cinco
 * itens dobram. Aquilo funcionou e foi preservado aqui.
 *
 * O que muda: a ficha agora diz de onde veio o dado, e o bloco do edital
 * deixa de mentir. Antes ele decidia entre "abrir na fonte" e "não foi
 * possível baixar" olhando se existia um LINK — então em 96 licitações
 * mostrava o texto confiante enquanto a IA só tinha visto os metadados.
 */
import React, { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { useSessao } from "../sessao";
import { api, ErroApi, type LicitacaoDetalhe } from "../api/cliente";
import { Decisao } from "./Decisao";
import { Disputa } from "./Disputa";
import { FormularioFeedback, PainelAnaliseHumana } from "./AnaliseHumana";
import { PainelLeituraIntegral } from "./PainelLeituraIntegral";
import { PainelAnalise } from "./PainelAnalise";
import { AtestadosDaOportunidade } from "./AtestadosDaOportunidade";
import { ArquivosPublicados, CamposDaFonte, DossiePendente, ItensDoDossie } from "./Dossie";
import { Secao } from "./Secao";
import { AtestadosDeclarados } from "./AtestadosDeclarados";
import { PainelScore } from "./PainelScore";
import { descartadaPeloFunil, Etiquetas, explicaEdital, Nota, rotuloEdital } from "./Selos";
import { dataHora, dia, fraseLegivel, nomeLegivel, prazoEm, situacaoDe, tiposDeServico } from "../formato";
import { IconeAlvo, IconeDocumento, IconeGlobo, IconeLocal, IconeMartelo, IconeMoeda, IconePessoa,
  IconeRelogio, IconeSeta } from "../ds";
import css from "./PainelDetalhe.module.css";

const A_VISTA = 4;

function Lista({ itens, risco }: { itens: string[]; risco?: boolean }) {
  if (!itens.length) return null;
  const dobra = itens.length > A_VISTA + 1;
  const visiveis = dobra ? itens.slice(0, A_VISTA) : itens;
  return (
    <>
      <ul className={`${css.itens} ${risco ? css.risco : ""}`}>
        {visiveis.map((x, i) => (
          <li key={i}>{x}</li>
        ))}
      </ul>
      {dobra && (
        <details className={css.mais}>
          <summary>mostrar mais {itens.length - A_VISTA}</summary>
          <ul className={`${css.itens} ${risco ? css.risco : ""}`}>
            {itens.slice(A_VISTA).map((x, i) => (
              <li key={i}>{x}</li>
            ))}
          </ul>
        </details>
      )}
    </>
  );
}



/**
 * Um atalho que, quando NAO da para usar, aparece assim mesmo — apagado e
 * dizendo por que.
 *
 * ── por que nao esconder ─────────────────────────────────────────────────
 *
 * Os tres condicionais desta barra sumiam quando a acao nao existia, e o
 * efeito era o pior possivel para quem le: a mesma licitacao mostrava quatro
 * botoes numa tela e tres na outra, sem explicacao. Quem nao conhece o sistema
 * conclui que quebrou; quem conhece fica na duvida se faltou dado ou se a
 * pagina nao carregou.
 *
 * Botao ausente nao ensina nada. Botao apagado com o motivo no hover responde
 * a pergunta seguinte — "e o que eu faco para ter isso?" — e e a mesma regra
 * que o resto do projeto segue: `NAO INDEXADO` em vez de "nao temos", `nulo`
 * em vez de `'[]'`. A ausencia tem de dizer de que tipo ela e.
 *
 * `aria-disabled` e nao `disabled`: elemento desabilitado de verdade sai da
 * ordem de foco e o leitor de tela pula — inclusive o motivo, que e a unica
 * coisa util ali.
 */
function Atalho({
  motivo,
  aoExplicar,
  className = "",
  children,
  ...resto
}: {
  /** Nulo quando o atalho funciona; a frase do porque quando nao. */
  motivo: string | null;
  /** O toque no atalho inativo: no toque nao ha hover, e o `title` nunca
   *  aparece. Quem monta mostra a frase (ver `.porqueToque`). */
  aoExplicar?: (motivo: string) => void;
  className?: string;
  children: React.ReactNode;
  href?: string;
  title?: string;
  target?: string;
  rel?: string;
}) {
  if (!motivo) {
    return (
      <a className={`${css.atalho} ${className}`} {...resto}>
        {children}
      </a>
    );
  }
  return (
    <span
      className={`${css.atalho} ${css.atalhoInativo} ${className}`}
      aria-disabled="true"
      title={motivo}
      onClick={() => aoExplicar?.(motivo)}
    >
      {children}
    </span>
  );
}

/**
 * Ha edital que a leitura consegue alcancar?
 *
 * `publicado` e `apagado` tem o link do arquivo na fonte (apagado e so a copia
 * local que sumiu); `lido` ja tem o texto. Nos outros tres o servidor
 * responderia com erro na hora: nao ha arquivo, a fonte recusa, ou ninguem
 * perguntou a ela.
 */
function podeGerar(d: LicitacaoDetalhe): boolean {
  return d.edital === "publicado" || d.edital === "apagado"
    || d.edital === "lido" || d.edital === "enviado";
}

// FORA DO COMPONENTE, de proposito. O painel lateral desmonta quando se troca
// de licitacao; com o "gerando" dentro de `useState`, voltar a esta mostraria o
// botao de novo, e o segundo clique poria a MESMA leitura para rodar duas vezes
// — a rota nao tem registro de leitura em curso para recusar.
const pedidas = new Map<string, number>();


function GerarPreAnalise({
  d,
  aoExplicar,
}: {
  d: LicitacaoDetalhe;
  aoExplicar?: (motivo: string) => void;
}) {
  const { podeOperar } = useSessao();
  const cliente = useQueryClient();
  const [erro, setErro] = useState<string | null>(null);

  // O ESTADO VEM DO SERVIDOR, e nao de um relogio aqui. Ate 2026-09-16 este
  // botao reperguntava `GET .../analise` esperando o 404 virar 200 — e quando a
  // leitura FALHAVA (a 2755 tinha o edital em ZIP) o 404 nunca mudava, e o
  // botao girava "Gerando..." por 45 minutos sem dizer nada. A rota de estado
  // le o log e o processo, e diz `falhou` na hora, com o motivo.
  //
  // Perguntar tambem ao ABRIR a licitacao, e nao so depois do clique: quem
  // recarrega a pagina no meio da leitura continua vendo "Gerando...", em vez
  // de um botao que convida a pedir a mesma leitura duas vezes.
  const pedidaEm = pedidas.get(d.id);
  const q = useQuery({
    queryKey: ["pre-analise-estado", d.id],
    queryFn: () => api.estadoGeracao(d.id),
    enabled: podeOperar,
  });
  // Resposta de ANTES do clique nao vale: sem isto, o "falhou" da tentativa
  // anterior apareceria no instante seguinte ao "Tentar de novo".
  const atual = pedidaEm !== undefined && q.dataUpdatedAt <= pedidaEm ? undefined : q.data;
  const gerando =
    (pedidaEm !== undefined && atual === undefined) || atual?.estado === "rodando";
  const falha = atual?.estado === "falhou" ? (atual.motivo ?? "a leitura falhou") : null;

  useQuery({
    queryKey: ["pre-analise-espera", d.id],
    queryFn: async () => {
      await cliente.invalidateQueries({ queryKey: ["pre-analise-estado", d.id] });
      return Date.now();
    },
    enabled: gerando,
    refetchInterval: 15_000,
  });

  useEffect(() => {
    if (atual?.estado === "pronta") {
      pedidas.delete(d.id);
      void cliente.invalidateQueries({ queryKey: ["licitacao", d.id] });
      void cliente.invalidateQueries({ queryKey: ["analise", d.id] });
      void cliente.invalidateQueries({ queryKey: ["licitacoes"] });
    }
  }, [atual?.estado, cliente, d.id]);

  const pede = useMutation({
    mutationFn: () => api.pedirAnalise(d.id),
    onMutate: () => setErro(null),
    onSuccess: () => {
      pedidas.set(d.id, Date.now());
      void cliente.invalidateQueries({ queryKey: ["pre-analise-estado", d.id] });
    },
    onError: (e) => {
      // 409 e "ja existe" ou "ja esta sendo gerada" (outra pessoa, a madrugada
      // ou o acompanhante chegou antes): nao e erro para quem clicou. Recarregar
      // o estado mostra a faixa "sendo gerada" ou o botao de baixar.
      if (e instanceof ErroApi && e.status === 409) {
        void cliente.invalidateQueries({ queryKey: ["licitacao", d.id] });
        void cliente.invalidateQueries({ queryKey: ["pre-analise-estado", d.id] });
        return;
      }
      setErro(
        e instanceof ErroApi && e.status === 403
          ? "Gerar pré-análise é permitido só para usuários com papel de operador."
          : e instanceof ErroApi && e.status === 422
          ? "Aprove esta licitação antes de gerar a pré-análise."
          : "Não foi possível começar a gerar. Tente de novo em instantes.",
      );
    },
  });

  // INATIVO COM MOTIVO para quem nao opera, e nao escondido: a rota recusa
  // leitor com 403, e um botao que aparece e falha no clique parece defeito.
  // Mesma forma dos outros atalhos inativos: tracejado, motivo no hover.
  if (!podeOperar) {
    const motivo =
      "Pré-análise ainda não gerada. Gerar é permitido só para usuários com papel de operador.";
    return (
      <span
        className={`${css.atalho} ${css.atalhoInativo}`}
        aria-disabled="true"
        title={motivo}
        onClick={() => aoExplicar?.(motivo)}
      >
        <IconeGerar />
        Gerar pré-análise
      </span>
    );
  }

  if (gerando || pede.isPending) {
    return (
      <span
        className={`${css.atalho} ${css.atalhoGerando}`}
        aria-busy="true"
        title="O edital está sendo lido. Costuma levar de 3 a 10 minutos; o botão de baixar aparece aqui sozinho quando terminar."
      >
        <IconeGerar girando />
        Gerando pré-análise…
      </span>
    );
  }

  // A FALHA APARECE NO PROPRIO BOTAO, e nao so no hover: "Tentar de novo" sem
  // dizer que falhou fazia parecer que o clique anterior nao tinha pegado. O
  // motivo inteiro fica no hover — e o que o log do servidor disse.
  const motivo = erro ?? (falha ? `A última tentativa falhou: ${falha}` : null);
  return (
    <button
      type="button"
      className={`${css.atalho} ${motivo ? css.atalhoFalhou : ""}`}
      title={
        motivo
          ? `${motivo}. Clique para tentar de novo.`
          : "Lê o edital desta licitação agora e gera a pré-análise."
      }
      onClick={() => pede.mutate()}
    >
      <IconeGerar />
      {motivo ? "Falhou · tentar de novo" : "Gerar pré-análise"}
    </button>
  );
}

/**
 * A FAIXA QUE CONFIRMA que a pre-analise esta sendo gerada — pedido da
 * gestao, 2026-09-16: o icone girando no botao nao provava nada, e uma leitura
 * de 10 minutos sem sinal nenhum parece travada.
 *
 * Tudo o que ela diz vem do SERVIDOR (`GET .../analise/estado`), e nao de um
 * relogio aqui: "rodando" so aparece se o processo da leitura esta vivo no
 * container, a etapa sai de quem e filho dele, e a contagem de documentos sai
 * do banco. Usa a mesma chave de cache do botao, entao os dois nunca
 * discordam e nao ha pergunta em dobro.
 */
function ProgressoPreAnalise({ d }: { d: LicitacaoDetalhe }) {
  const { podeOperar } = useSessao();
  const { data } = useQuery({
    queryKey: ["pre-analise-estado", d.id],
    queryFn: () => api.estadoGeracao(d.id),
    enabled: podeOperar,
  });
  if (!data) return null;

  if (data.estado === "falhou") {
    return (
      <div className={`${css.progresso} ${css.progressoFalhou}`} role="status">
        <strong>Não foi possível gerar a pré-análise.</strong>
        <span>{data.motivo ?? "A leitura terminou sem gravar resultado."}</span>
        <span className={css.progressoNota}>
          Use “Falhou · tentar de novo” para pedir outra vez.
        </span>
      </div>
    );
  }
  if (data.estado !== "rodando") return null;

  const minutos = data.minutos ?? 0;
  const ha = minutos < 1 ? "agora há pouco" : minutos === 1 ? "há 1 minuto" : `há ${minutos} minutos`;
  const docs = data.documentos_lidos ?? 0;
  const lidos = docs === 1 ? "1 documento lido" : `${docs} documentos lidos`;
  const etapa =
    data.etapa === "modelo"
      ? `Etapa 2 de 2 — a IA está analisando o edital (${lidos}). É a etapa mais demorada.`
      : data.etapa === "ocr"
        ? `Etapa 1 de 2 — lendo um documento escaneado, página por página (${lidos} até agora). Pode levar alguns minutos.`
        : data.etapa === "documentos"
          ? `Etapa 1 de 2 — baixando e lendo os documentos do edital (${lidos} até agora).`
          : `Em andamento no servidor (${lidos}).`;

  return (
    <div className={css.progresso} role="status" aria-live="polite">
      <strong>
        <IconeGerar girando /> Pré-análise sendo gerada — começou {ha}
      </strong>
      <span>{etapa}</span>
      <span className={css.progressoNota}>
        Confirmado pelo servidor e atualizado a cada 15 segundos. Pode sair desta
        tela: a pré-análise continua sendo gerada e o botão de baixar aparece
        quando ela ficar pronta.
      </span>
    </div>
  );
}

/**
 * Por que a pre-analise nao esta disponivel, na lingua de quem le.
 *
 * A causa raiz e sempre a mesma — o edital nao foi lido — mas o MOTIVO de nao
 * ter sido lido muda o que a pessoa pode fazer a respeito, e e isso que
 * interessa. Nao ha arquivo? Nada a fazer. Ninguem perguntou a fonte? Falta
 * rodar o dossie. O arquivo existe e ninguem leu? Falta aprovar a licitacao,
 * porque a leitura custa dinheiro e so roda depois disso.
 */
function porQueSemPreAnalise(d: LicitacaoDetalhe): string | null {
  // `tem_analise` e nao `nivel`, e a diferenca importou em producao.
  //
  // `nivel` so enxerga a leitura da versao VIVA, e o cartao usa isso com
  // razao: mostrar o veredito de uma leitura antiga como se fosse de agora
  // seria mentir. Mas o PDF pode ser servido datado — e quando a versao mudou,
  // em 2026-09-15, as 94 leituras ja pagas ficaram sob o rotulo anterior e o
  // botao apagou em TODAS as oportunidades.
  //
  // Esconder trabalho pago porque o rotulo mudou e pior que o problema que o
  // rotulo resolve. `analise_de` recua para a leitura mais recente, e o botao
  // acompanha.
  if (d.tem_analise) return null;
  const base = "Pré-análise indisponível. ";

  // A DESCARTADA PELO FUNIL NAO PASSA POR IA (decisao de 2026-09-22), nem
  // pelo botao — e a rota recusa com 422. Vem antes de tudo: e a causa.
  if (descartadaPeloFunil(d.motivo_aba)) {
    return base + `${d.motivo_aba?.texto}: por decisão da Nectar, licitação ` +
      "descartada pelo funil não passa por IA.";
  }

  // O PRAZO VEM ANTES DO ESTADO DO EDITAL, e essa ordem conserta uma mentira
  // que eu mesmo escrevi uma hora atras: a mensagem padrao dizia "esta na fila
  // da leitura automatica" para TODAS as que tem edital, inclusive as ja
  // encerradas — que sao 295 das 440, e que nunca serao lidas, porque a fila
  // corta por prazo.
  //
  // Prometer uma leitura que nao vai acontecer e pior que nao explicar nada:
  // quem le espera, e a espera nao termina.
  const fim = d.encerramento ? new Date(d.encerramento) : null;
  if (fim && !Number.isNaN(fim.getTime()) && fim < new Date()) {
    return base + "O prazo desta licitação já encerrou, e a leitura do " +
      "edital só roda no que ainda pode virar proposta.";
  }
  switch (d.edital) {
    case "ausente":
      return base + "A fonte não publicou arquivo de edital para esta " +
        "licitação, então não houve o que ler.";
    case "nao_perguntado":
      return base + "Ninguém perguntou à fonte se há edital publicado. " +
        "Não é o mesmo que não haver — falta rodar o dossiê.";
    case "indisponivel":
      return base + "A fonte recusou o download do edital, normalmente por " +
        "exigir login próprio ou JavaScript.";
    case "sem_caminho":
      return base + "Nenhuma fonte desta licitação leva ao edital, e ninguém " +
        "o enviou à mão. Obtenha o edital por fora e mande-o pela tela Editais.";
    default:
      // SEM FALAR EM DINHEIRO, por pedido da Nectar, e ela tem razao: o
      // custo e assunto interno, e quem le a tela quer saber o que falta,
      // nao o que a empresa gasta. Dizer "custa US$ 1,40" a quem nao pode
      // fazer nada a respeito so transmite que o sistema esta racionando.
      //
      // Desde a 0146 (22/10) a pre-analise so sai do botao: prometer "a
      // proxima rodada" seria a espera que nao termina.
      return base + "O edital ainda não foi lido. Use “Gerar pré-análise” " +
        "para ler o edital desta oportunidade.";
  }
}


/**
 * O ENCERRAMENTO, como indicador do cabeçalho: quanto falta, e a data e a hora
 * por baixo.
 *
 * "faltam N dias" e nao so a data: obriga a pessoa a fazer a conta de cabeca,
 * e a conta de cabeca com data no formato do banco e exatamente o tipo de
 * esforco que faz alguem desistir de ler. A conta e por dia de calendario
 * (`prazoEm`): uma sessao amanha as 9h e "amanha", e nao "hoje" porque faltam
 * 19 horas. Vermelho discreto so para o que ja passou; ambar para a semana.
 */
function IndicadorPrazo({
  quando,
  abertura,
}: {
  quando: string | null | undefined;
  abertura?: string | null;
}) {
  const p = prazoEm(quando);
  const cor =
    p.nivel === "vencido" ? css.corVencido : p.nivel === "perto" ? css.corPerto : "";
  const texto =
    p.nivel === "vencido" ? "Encerrada" :
    p.nivel === "perto" && p.texto === "hoje" ? "Encerra HOJE" :
    p.nivel === "perto" && p.texto === "amanhã" ? "Encerra amanhã" :
    p.nivel === "semData" ? "Sem prazo informado" :
    `Faltam ${p.texto.replace("em ", "")}`;
  // O PRAZO ABRE A FAIXA (28/10, a ordem do Licitário), e a cor do estado
  // fica só no texto, como sempre.
  const sub = [
    quando ? `até ${dataHora(quando)}` : null,
    abertura ? `abertura ${dataHora(abertura)}` : null,
  ].filter(Boolean).join(" · ");
  return (
    <div className={css.indicador}>
      <span className={css.indicadorRotulo}><IconeRelogio tamanho={16} />Prazo das propostas</span>
      <b className={`${css.indicadorValor} ${cor} ${p.nivel === "semData" ? css.corVazio : ""}`}>{texto}</b>
      {sub ? <span className={css.indicadorSub}>{sub}</span> : null}
    </div>
  );
}

function Indicador({
  icone,
  rotulo,
  valor,
  sub,
  vazio,
}: {
  /** O ícone pequeno antes do rótulo, na cor apagada (28/10, pedido do chefe
   *  do Pedro: as informações com pequenos ícones, como no Licitário). */
  icone?: React.ReactNode;
  rotulo: string;
  valor: string | null | undefined;
  sub?: React.ReactNode;
  /** O que dizer quando não há valor: "não informado", e não um espaço. */
  vazio: string;
}) {
  return (
    <div className={css.indicador}>
      <span className={css.indicadorRotulo}>{icone}{rotulo}</span>
      <b className={`${css.indicadorValor} ${valor ? "" : css.corVazio}`}>{valor || vazio}</b>
      {sub ? <span className={css.indicadorSub}>{sub}</span> : null}
    </div>
  );
}

/**
 * UMA PERGUNTA DO RESUMO (28/10): a pergunta pequena e apagada, a resposta em
 * destaque (com a cor do estado, só no texto), a frase de apoio e o caminho
 * para a aba que a explica. Sem caixa, como os dados do cabeçalho. A ideia
 * veio do Licitário ("O que precisa? / Até quando? / Onde disputo?"), com as
 * perguntas que importam aqui: escopo, edital e decisão.
 */
function Pergunta({
  icone,
  pergunta,
  resposta,
  tom,
  apoio,
  acao,
}: {
  icone?: React.ReactNode;
  pergunta: string;
  resposta: string;
  tom?: "ok" | "atencao" | "nao" | "neutro";
  apoio?: React.ReactNode;
  acao?: { rotulo: string; aoClicar: () => void };
}) {
  return (
    <div className={css.pergunta}>
      <dt className={css.perguntaRotulo}>{icone}{pergunta}</dt>
      <dd className={css.perguntaCorpo}>
        <b className={`${css.perguntaResposta} ${tom ? css[`etapa_${tom}`] : ""}`}>{resposta}</b>
        {apoio ? <span className={css.perguntaApoio}>{apoio}</span> : null}
        {acao ? (
          <button type="button" className={css.perguntaAcao} onClick={acao.aoClicar}>
            {acao.rotulo} <IconeSeta tamanho={13} />
          </button>
        ) : null}
      </dd>
    </div>
  );
}

const ADERENCIA: Record<string, { texto: string; tom: "ok" | "atencao" | "nao" | "neutro" }> = {
  alta: { texto: "Sim, aderência alta", tom: "ok" },
  media: { texto: "Provável, aderência média", tom: "neutro" },
  baixa: { texto: "Duvidoso, aderência baixa", tom: "atencao" },
  nenhuma: { texto: "Não, fora do escopo", tom: "nao" },
};

/**
 * O VALOR, COM O ZERO DISTINTO DO AUSENTE (19/10): `null` é "a fonte não
 * informou" e "R$ 0,00" é um valor que a fonte mandou — orçamento sigiloso ou
 * contratação sem valor estimado. As duas coisas ficam ditas, e nenhuma
 * vira a outra.
 *
 * E DE ONDE VEIO (0149, `funil/valor.py`): o orçamento SIGILOSO que o órgão
 * declarou em todos os itens no PNCP é dito como tal, com a prova; o valor
 * que saiu da soma dos itens publicados, ou que a linha tinha perdido e veio
 * de outra publicação, traz a frase que o prova embaixo.
 */
function IndicadorValor({ valor, origem, prova }: {
  valor: string | null | undefined;
  origem?: "fonte" | "itens" | "sigiloso" | null;
  prova?: string | null;
}) {
  const zerado = !!valor && /^R\$\s*0([.,]0+)?$/.test(valor.trim());
  const sigiloso = origem === "sigiloso";
  const frase = prova ? prova.charAt(0).toUpperCase() + prova.slice(1) : null;
  return (
    <Indicador
      icone={<IconeMoeda tamanho={16} />}
      rotulo="Valor estimado"
      valor={sigiloso && (zerado || !valor) ? null : valor}
      vazio={sigiloso ? "Orçamento sigiloso" : "Não informado"}
      sub={frase ?? (zerado ? "Valor zerado na fonte — não é o mesmo que não informado" : null)}
    />
  );
}

/** As cinco abas, nesta ordem, no painel e na página própria. */
type Aba = "visao" | "edital" | "atestados" | "documentos" | "historico";

const ABAS: { chave: Aba; rotulo: string }[] = [
  { chave: "visao", rotulo: "Visão geral" },
  { chave: "edital", rotulo: "Edital e requisitos" },
  { chave: "atestados", rotulo: "Atestados" },
  { chave: "documentos", rotulo: "Documentos e evidências" },
  { chave: "historico", rotulo: "Histórico" },
];

/**
 * A ABA ATIVA MORA NA URL (`?aba=`), e é por isso que o painel e a página
 * própria a compartilham: "Abrir em tela dedicada" leva a aba junto, e recarregar
 * ou voltar a devolve. `replace`, para trocar de aba não encher o histórico do
 * navegador. Os outros parâmetros (os filtros da lista, o card aberto) ficam
 * como estão.
 */
export function useAba(): [Aba, (a: Aba) => void] {
  const [params, setParams] = useSearchParams();
  const v = params.get("aba");
  const aba = ABAS.find((a) => a.chave === v)?.chave ?? "visao";
  const muda = (a: Aba) =>
    setParams(
      (atual) => {
        const p = new URLSearchParams(atual);
        p.set("aba", a);
        return p;
      },
      { replace: true },
    );
  return [aba, muda];
}

/**
 * O DETALHE DE UMA OPORTUNIDADE — UM COMPONENTE SÓ, para o painel ao lado da
 * lista e para a página própria (19/10). O que muda entre os dois não é o
 * conteúdo, e sim o espaço: as colunas se reorganizam pela largura do próprio
 * detalhe (`container-type`, no CSS), e não pela da janela.
 *
 * A ordem: o cabeçalho (o que é, de quem, e o score no canto), os três dados
 * que decidem, as ações com documento, as cinco abas e, depois da aba aberta, o
 * bloco de registrar a decisão. A decisão mora FORA das abas de propósito: o
 * que se escreve nela sobrevive à troca de aba.
 *
 * Quem monta passa `key={d.id}`: cada oportunidade tem o próprio estado, e o
 * formulário de uma nunca aparece na outra.
 */
export function Corpo({
  d,
  telaCheia = false,
}: {
  d: LicitacaoDetalhe;
  /** A página própria: muda a escala do título, e nada mais. */
  telaCheia?: boolean;
}) {
  const urlEdital = editalPublicado(d.arquivos);
  const [feedbackAberto, setFeedbackAberto] = useState(false);
  // O porque do atalho apagado que foi tocado. So aparece onde nao ha hover.
  const [porque, setPorque] = useState<string | null>(null);
  const [aba, setAba] = useAba();
  // AS ABAS JÁ VISITADAS FICAM MONTADAS (escondidas): o que foi digitado num
  // formulário — o atestado indicado, por exemplo — não se perde ao trocar de
  // aba, e a aba que nunca foi aberta não custa uma consulta sequer.
  const [visitadas, setVisitadas] = useState<Aba[]>([aba]);
  useEffect(() => {
    setVisitadas((v) => (v.includes(aba) ? v : [...v, aba]));
    // A barra rola no celular: a aba ativa vem ao centro dela. À mão, e só no
    // eixo horizontal — `scrollIntoView` rolaria a página para mostrar a barra.
    const el = document.getElementById(`aba-${aba}`);
    const barra = el?.parentElement;
    if (el && barra && barra.scrollWidth > barra.clientWidth) {
      barra.scrollTo({
        left: el.offsetLeft - (barra.clientWidth - el.offsetWidth) / 2,
        behavior: "smooth",
      });
    }
  }, [aba]);

  const tipo = tiposDeServico(d, 4);
  const situacao = situacaoDe(d);
  const aGerar = !d.tem_analise && podeGerar(d) && !descartadaPeloFunil(d.motivo_aba);

  const painel = (chave: Aba, conteudo: React.ReactNode) =>
    visitadas.includes(chave) ? (
      <div
        role="tabpanel"
        id={`painel-${chave}`}
        aria-labelledby={`aba-${chave}`}
        hidden={aba !== chave}
        className={css.painelAba}
      >
        {conteudo}
      </div>
    ) : null;

  // O TEXTO DA FONTE LEGÍVEL (28/10): o órgão e o objeto chegam em caixa alta
  // e sem acento; o município do registro devolve a grafia certa aos dois.
  const municipio = nomeLegivel(d.municipio);
  const orgao = nomeLegivel(d.orgao, [municipio]);
  const objeto = fraseLegivel(d.objeto, [municipio]);
  const local = [municipio, d.uf].filter(Boolean).join(" / ");
  const onde = d.sistema || d.fonte;
  const aderencia = d.aderencia ? ADERENCIA[d.aderencia] : undefined;
  const contas: Partial<Record<Aba, number>> = { documentos: d.arquivos.length };

  return (
    <div className={`${css.corpo} ${telaCheia ? css.pagina : ""}`}>
      {/* A ORGANIZAÇÃO DO LICITÁRIO, NO VISUAL DO NECTUNO (28/10, pedido do
          chefe do Pedro: "gostou da forma do layout de organização desse
          site"; e o Pedro, depois da primeira versão em cartões: "tá muito
          genérica… adapte com o visual clean do nosso sistema"). De lá veio a
          ORDEM, e nada do desenho:
            · o objeto logo abaixo do órgão, como o resumo do cartão da lista;
            · os dados que decidem numa faixa entre filetes, com o PRAZO
              abrindo a faixa;
            · as abas que acompanham a rolagem, na página;
            · na página larga, DUAS COLUNAS: o conteúdo da aba à esquerda e, à
              direita, atrás de um filete, o que se FAZ — a decisão e os
              documentos.
          O desenho é o de sempre: uma folha só, texto sem caixa, filetes, o
          azul só no que se clica, a cor de estado só no texto. No painel ao
          lado da lista (estreito) tudo empilha na ordem de antes. As colunas
          se decidem pela largura do PRÓPRIO detalhe (`@container`). */}
      <div className={css.grade}>
        <header className={css.cabecalho}>
          {/* O CABEÇALHO DO CARTÃO DA LISTA, em ponto maior: o tipo e a
              situação atrás do filete; o órgão; o local e o código; o resumo do
              objeto. O score no canto, atrás do filete, uma vez só. */}
          <div className={css.identidade}>
            <div className={css.identificacao}>
              <p className={css.categoria}>
                <span className={tipo.classificado ? css.tipo : css.semTipo}
                      title={tipo.classificado ? "Tipo de serviço da oportunidade" : undefined}>
                  {tipo.nomes.join(" · ")}
                </span>
                {situacao ? (
                  <span className={`${css.etapa} ${css[`etapa_${situacao.tom}`]}`}>
                    {situacao.texto}
                  </span>
                ) : null}
              </p>
              <h2 className={css.orgao}>{orgao || "Órgão não informado"}</h2>
              <p className={css.local}>
                <span><IconeLocal tamanho={16} />{local || "Local não informado"}</span>
                {d.codigo ? <span className={css.codigo}><IconeDocumento tamanho={16} />Edital {d.codigo}</span> : null}
              </p>
              <p className={css.resumo} title={objeto}>{objeto}</p>
            </div>
            <div className={css.placar}>
              <Nota valor={d.score} faixa={d.faixa} decisao={d.decisao}
                    recomendacao={d.recomendacao} motivo={d.motivo_aba} />
            </div>
          </div>

          <div className={css.indicadores}>
            <IndicadorPrazo quando={d.encerramento} abertura={d.abertura} />
            <IndicadorValor valor={d.valor_estimado} origem={d.valor_origem}
                            prova={d.valor_prova} />
            <Indicador
              icone={<IconeGlobo tamanho={16} />}
              rotulo="Onde se disputa"
              valor={onde}
              vazio="Não informado"
              sub={d.url_edital ? (
                <a href={d.url_edital} target="_blank" rel="noopener noreferrer" className={css.indicadorLink}>
                  Abrir no portal ↗
                </a>
              ) : d.fonte && d.fonte !== onde ? `pela ${d.fonte}` : null}
            />
            <Indicador icone={<IconeMartelo tamanho={16} />} rotulo="Modalidade" valor={d.modalidade}
                       sub={d.modo_disputa ? `Disputa ${d.modo_disputa.toLowerCase()}` : null}
                       vazio="Não informada" />
          </div>

          {/* OS ALERTAS: as duas etiquetas da regra (o Pedro, 14/10) e a frase
              inteira do motivo da aba — por que um score alto está em
              Descartadas. Hover nao se descobre sem saber que ele existe. */}
          {d.etiquetas?.length || d.motivo_aba ? (
            <div className={css.alertas}>
              {d.etiquetas?.length ? (
                <div className={css.selos}>
                  <Etiquetas itens={d.etiquetas} />
                </div>
              ) : null}
              {d.motivo_aba ? <p className={css.motivoAba}>{d.motivo_aba.ajuda}</p> : null}
            </div>
          ) : null}
        </header>

      <aside className={css.lateral} aria-label="Decisão e documentos">
      {/* REGISTRAR A DECISÃO, NO ALTO (20/10, pedido do Pedro): é o que a
          pessoa veio fazer, e no pé da página ficava depois de cinco abas. Logo
          abaixo do retrato (score, valor, prazo, alertas) e antes dos
          documentos, destacada com o azul do sistema. Fora das abas: o motivo
          que se escreve aqui sobrevive à troca de aba e à abertura da página
          própria (o rascunho mora por oportunidade, em `Decisao`). Na página
          larga ela abre a coluna da direita, que acompanha a rolagem. */}
      <section className={css.decisao} aria-labelledby={`decisao-${d.id}`}>
        <h3 id={`decisao-${d.id}`}>Registrar decisão</h3>
        <Decisao key={d.id} d={d} />
      </section>

      {/* AS AÇÕES COM DOCUMENTO, cada uma uma vez. Elas quebram de linha no
          painel estreito; na coluna da direita viram uma lista, um por linha.
          A que não dá para usar aparece apagada, com o porquê: botão ausente
          não ensina nada. */}
      <div className={css.acoes} role="group" aria-label="Documentos e feedback">
        <p className={css.acoesTitulo}>Documentos</p>
        <div className={css.atalhos}>
          <Atalho
            aoExplicar={setPorque}
            motivo={d.url_edital ? null
              : "Endereço indisponível. A fonte não informou o link desta "
                + "licitação no portal de origem."}
            href={d.url_edital ?? undefined}
            target="_blank"
            rel="noopener noreferrer"
            title="O anúncio no portal de origem, fora do sistema."
          >
            <IconeFonte />
            Site do licitante
          </Atalho>
          <Atalho
            aoExplicar={setPorque}
            motivo={urlEdital ? null
              : d.arquivos.length
                ? "Edital indisponível. A fonte publicou "
                  + `${d.arquivos.length} arquivo(s), e nenhum está `
                  + "identificado como edital. A lista completa fica na aba "
                  + "Documentos e evidências."
                : "Edital indisponível. " + explicaEdital(d.edital)}
            href={urlEdital ?? undefined}
            target="_blank"
            rel="noopener noreferrer"
            title="O edital publicado pela fonte, como ela o publicou."
          >
            <IconeEdital />
            Baixar edital
          </Atalho>
          {/* GERAR NO LUGAR DE BAIXAR, quando ainda nao ha o que baixar e ha
              edital para ler. */}
          {aGerar ? (
            <GerarPreAnalise d={d} aoExplicar={setPorque} />
          ) : (
            <Atalho
              aoExplicar={setPorque}
              motivo={porQueSemPreAnalise(d)}
              href={`/api/licitacoes/${d.id}/oportunidade.pdf`}
              title="A análise de viabilidade em PDF."
              // Numa aba nova (15/10, pedido do Pedro): o PDF abria no lugar
              // do Nectuno, e voltar à oportunidade exigia o "voltar".
              target="_blank"
              rel="noopener noreferrer"
            >
              <IconeBaixar />
              Baixar pré-análise
            </Atalho>
          )}
          <button
            type="button"
            className={`${css.atalho} ${feedbackAberto ? css.atalhoAberto : ""}`}
            aria-expanded={feedbackAberto}
            title="Anexar uma análise escrita por uma pessoa."
            onClick={() => setFeedbackAberto((v) => !v)}
          >
            <IconeEnviar />
            {feedbackAberto ? "Fechar feedback" : "Feedback da análise"}
          </button>
        </div>

        {/* O motivo do atalho apagado, no toque: o `title` nao aparece sem
            hover, e o dedo tocava sem nada acontecer. */}
        {porque ? (
          <p className={css.porqueToque} role="status">
            {porque}
          </p>
        ) : null}

        {!d.tem_analise && podeGerar(d) ? <ProgressoPreAnalise d={d} /> : null}

        {feedbackAberto ? <FormularioFeedback d={d} /> : null}
      </div>
      </aside>

      {/* AS CINCO ABAS, nesta ordem, nos dois modos. Nomes inteiros, e elas
          quebram em mais de uma linha quando o painel é estreito. Na página
          elas ficam numa barra própria, que gruda no alto ao rolar. */}
      <div className={css.coluna}>
      <div className={css.abas} role="tablist" aria-label="Seções da oportunidade">
        {ABAS.map((a, n) => (
          <button
            key={a.chave}
            type="button"
            role="tab"
            id={`aba-${a.chave}`}
            aria-selected={aba === a.chave}
            aria-controls={`painel-${a.chave}`}
            tabIndex={aba === a.chave ? 0 : -1}
            className={css.aba}
            onClick={() => setAba(a.chave)}
            onKeyDown={(e) => {
              const passo = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
              if (!passo) return;
              e.preventDefault();
              const prox = ABAS[(n + passo + ABAS.length) % ABAS.length]!;
              setAba(prox.chave);
              document.getElementById(`aba-${prox.chave}`)?.focus();
            }}
          >
            {a.rotulo}
            {contas[a.chave] ? <span className={css.abaConta}>{contas[a.chave]}</span> : null}
          </button>
        ))}
      </div>

      <div className={css.principal}>
      {/* O FIM DO FUNIL (D13): só aparece na aprovada — preparar a
          documentação, informar a disputa, informar o resultado. */}
      <Disputa d={d} />

      {/* ── 1 · VISÃO GERAL ─────────────────────────────────────────────────── */}
      {painel(
        "visao",
        <>
          {/* EM RESUMO (28/10): três perguntas respondidas de relance, cada
              uma com o caminho para a aba que a explica. */}
          <Secao titulo="Em resumo">
          <dl className={css.perguntas}>
            <Pergunta
              icone={<IconeAlvo tamanho={16} />}
              pergunta="É do escopo do Nectar?"
              resposta={aderencia?.texto ?? "Ainda não triada"}
              tom={aderencia?.tom ?? "neutro"}
              apoio={d.categoria ? `Linha: ${d.categoria}` : "A triagem lê o objeto contra o escopo."}
              acao={{ rotulo: "Ver os atestados", aoClicar: () => setAba("atestados") }}
            />
            <Pergunta
              icone={<IconeDocumento tamanho={16} />}
              pergunta="O edital foi lido?"
              resposta={rotuloEdital(d.edital).replace(/^./, (c) => c.toUpperCase())}
              tom={d.edital === "lido" || d.edital === "enviado" ? "ok"
                : d.edital === "lido_com_lacunas" || d.edital === "incompleto"
                  || d.edital === "sem_caminho" || d.edital === "indisponivel" ? "atencao" : "neutro"}
              apoio={d.arquivos.length
                ? `${d.arquivos.length} ${d.arquivos.length === 1 ? "arquivo publicado" : "arquivos publicados"} pela fonte`
                : "Nenhum arquivo publicado pela fonte"}
              acao={{ rotulo: "Ver os documentos", aoClicar: () => setAba("documentos") }}
            />
            <Pergunta
              icone={<IconePessoa tamanho={16} />}
              pergunta="Já foi decidida?"
              resposta={d.decisao === "aprovada" ? "Aprovada"
                : d.decisao === "descartada" ? "Reprovada" : "Aguarda decisão"}
              tom={d.decisao === "aprovada" ? "ok" : d.decisao === "descartada" ? "nao" : "neutro"}
              apoio={d.decidido_por
                ? `por ${d.decidido_por}${d.decidido_em ? ` em ${dia(d.decidido_em)}` : ""}`
                : "Ninguém registrou decisão ainda."}
              acao={{ rotulo: "Ver o histórico", aoClicar: () => setAba("historico") }}
            />
          </dl>
          </Secao>

          <Secao titulo="Objeto da contratação">
            <p className={css.objeto}>{objeto}</p>
            {d.termos.length > 0 && (
              <p className={css.origem}>
                Encontrada por{" "}
                {d.termos.map((t) => (
                  <span key={t} className={css.chip}>
                    {t}
                  </span>
                ))}
              </p>
            )}
          </Secao>

          {/* O PARECER, em rubricas como o resto da aba (28/10): o Veredito
              abre, e a Triagem do objeto fecha (PainelAnalise). */}
          <PainelAnalise d={d} parte="parecer" />

            {/* CONTINUAR, na mesma margem das rubricas do parecer. */}
            <nav className={css.atalhosDaAba} aria-label="Continuar a análise">
              <span className={css.atalhosRubrica}>Continuar</span>
              <button type="button" onClick={() => setAba("edital")}>Ficha do edital <IconeSeta tamanho={13} /></button>
              <button type="button" onClick={() => setAba("edital")}>Exigências <IconeSeta tamanho={13} /></button>
              <button type="button" onClick={() => setAba("atestados")}>Atestados <IconeSeta tamanho={13} /></button>
            </nav>
        </>,
      )}

      {/* ── 2 · EDITAL E REQUISITOS ────────────────────────────────────────── */}
      {painel(
        "edital",
        <>
          <Secao titulo="Ficha do edital">
            <PainelAnalise d={d} parte="ficha" />
          </Secao>

          <Secao titulo="Cobertura das exigências">
            <PainelLeituraIntegral id={d.id} parte="cobertura" />
          </Secao>

          {d.exigencias_habilitacao.length > 0 && (
            <Secao titulo="Exigências de habilitação" conta={d.exigencias_habilitacao.length}>
              <Lista itens={d.exigencias_habilitacao} />
            </Secao>
          )}
          {d.itens_principais.length > 0 && (
            <Secao titulo="Itens do objeto" conta={d.itens_principais.length}>
              <Lista itens={d.itens_principais} />
            </Secao>
          )}

          {/* Grupos e valor, documentação exigida, custos e plano: quatro
              rubricas próprias (PainelAnalise). */}
          <PainelAnalise d={d} parte="edital" />

          {d.itens.length > 0 && <ItensDoDossie itens={d.itens} />}
        </>,
      )}

      {/* ── 3 · ATESTADOS ──────────────────────────────────────────────────── */}
      {painel(
        "atestados",
        <>
          {/* O ATESTADO QUE A NECTAR INDICA (D11) vem antes da conta, porque
              muda a conta: quem indica vê o score refeito logo abaixo. */}
          <AtestadosDeclarados d={d} />

          <Secao titulo="Atestados desta oportunidade">
            <AtestadosDaOportunidade d={d} />
          </Secao>

          <Secao titulo="Como o score foi calculado">
            <PainelScore id={d.id} />
          </Secao>
        </>,
      )}

      {/* ── 4 · DOCUMENTOS E EVIDÊNCIAS ────────────────────────────────────── */}
      {painel(
        "documentos",
        <>
          {d.arquivos.length > 0 ? (
            <ArquivosPublicados arquivos={d.arquivos} />
          ) : (
            <Secao titulo="Arquivos publicados">
              <p className={css.prosa}>A fonte não tem arquivo publicado para esta licitação.</p>
            </Secao>
          )}
          {/* O aviso vem do estado real do edital, não da existência do link,
              e a frase vem do mesmo mapa que o selo usa. */}
          {d.edital !== "lido" && (
            <Secao titulo="Situação do edital">
              <p className={css.prosa}>{explicaEdital(d.edital)}</p>
            </Secao>
          )}
          <DossiePendente d={d} />

          {d.evidencia ? (
            <Secao titulo="Evidência no objeto">
                <blockquote
                  className={`${css.evidencia} ${d.evidencia_ok ? "" : css.evidenciaRuim}`}
                >
                  {d.evidencia}
                </blockquote>
                <p className={css.evidenciaNota}>
                  {d.evidencia_ok
                    ? "Trecho conferido: existe literalmente no objeto. A conferência é feita por código, comparando texto — não depende de confiar no modelo."
                    : "Este trecho NÃO aparece literalmente no objeto. É alucinação, e o registro vai para revisão humana independentemente do veredito."}
                </p>
            </Secao>
          ) : null}

          <Secao titulo="Evidências no edital">
            <PainelAnalise d={d} parte="evidencias" />
          </Secao>

          <Secao titulo="Rastreabilidade">
            <PainelLeituraIntegral id={d.id} parte="rastreio" />
          </Secao>

          <Secao titulo="Metodologia da leitura">
            <details className={css.tecnico}>
              <summary>Ver os detalhes técnicos de processamento</summary>
              <PainelAnalise d={d} parte="rastreio" />
            </details>
          </Secao>

          <CamposDaFonte d={d} />
        </>,
      )}

      {/* ── 5 · HISTÓRICO ──────────────────────────────────────────────────── */}
      {painel(
        "historico",
        <>
          <HistoricoDeAvaliacao id={d.id} />

          {d.historico.length > 0 && (
            <Secao titulo="Histórico de análise" conta={d.historico.length}>
              <ul className={css.historico}>
                {d.historico.map((h, i) => (
                  <li key={i}>
                    <b>{h.score ?? "—"}</b> {h.nivel} · {h.modelo} · {dia(h.criado_em)}
                  </li>
                ))}
              </ul>
            </Secao>
          )}

          <PainelAnaliseHumana d={d} />
        </>,
      )}
      </div>
      </div>
      </div>
    </div>
  );
}

/**
 * Quem avaliou esta licitacao, e o que decidiu — TODAS as vezes.
 *
 * O que o painel ja chamava de "Historico de analise" e o historico das
 * passadas da IA (nivel, modelo, score). Este e o das PESSOAS, e nao existia
 * em tela nenhuma: `GET /api/licitacoes/{id}/decisao` e `api.decisoesDe`
 * existem, estao testados, e nenhum componente jamais os chamou.
 *
 * O CARTAO DA LISTA MOSTRA SO A MAIS RECENTE, porque o SQL de la tem
 * `limit 1`. Aqui aparecem todas, e e o ponto: `decisao` e append-only de
 * proposito, e quem aprovou e depois descartou e o caso mais informativo que
 * a tabela guarda. Um card que dissesse "voce ja avaliou" com base so na
 * ultima mentiria toda vez que outra pessoa decidisse depois.
 */
function HistoricoDeAvaliacao({ id }: { id: string }) {
  const { data } = useQuery({
    queryKey: ["decisoes", id],
    queryFn: () => api.decisoesDe(id),
  });
  const avaliacoes = data ?? [];
  return (
    <Secao titulo="Decisões e justificativas" conta={avaliacoes.length}>
      {avaliacoes.length === 0 ? (
        // O VAZIO É DITO: sumir faria "ninguém decidiu" parecer "não carregou".
        <p className={css.prosa}>Nenhuma decisão registrada para esta oportunidade ainda.</p>
      ) : (
        <ul className={css.historico}>
          {avaliacoes.map((a) => (
            <li key={a.id}>
              <b>{a.veredito === "aprovada" ? "Aprovada" : "Reprovada"}</b> por{" "}
              {a.decidido_por} · {dia(a.decidido_em)}
              {/* O score que a pessoa TINHA NA TELA, gravado junto com o
                  veredito e nao consultado depois. Nulo nas decisoes anteriores
                  a 2026-09-14, quando o codigo gravava `null` fixo. */}
              {a.score != null ? ` · score ${a.score}` : ""}
              {a.motivo ? <div className={css.prosa}>{a.motivo}</div> : null}
            </li>
          ))}
        </ul>
      )}
    </Secao>
  );
}

/* ── os icones dos atalhos ────────────────────────────────────────────────
 *
 * Desenhados aqui, em SVG de 14px com `currentColor`, e nao emoji nem fonte de
 * icones: emoji muda de forma e de cor a cada sistema — no Windows sai colorido
 * e desalinhado com o texto ao lado —, e uma fonte de icones seria uma
 * dependencia nova para quatro desenhos.
 *
 * `currentColor` e o que faz o icone acompanhar o estado do botao: no principal
 * ele fica com a cor de realce, no hover acompanha, sem nenhuma regra extra.
 */
function Svg({ children }: { children: React.ReactNode }) {
  return (
    <svg
      viewBox="0 0 16 16"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

/**
 * A URL do arquivo que E o edital, quando da para afirmar qual e.
 *
 * `arquivos` e a lista publicada pela fonte, e ela mistura o edital com termo
 * de referencia, anexos, planilhas e avisos. Nao ha campo que diga "este e o
 * edital", entao o que existe e o nome — e o nome so serve se for exigido:
 * devolver o primeiro arquivo da lista quando nenhum se chama edital acertaria
 * as vezes e erraria calado nas outras.
 *
 * Por isso NAO ha desempate por posicao nem fallback. Sem "edital" no titulo ou
 * no tipo, esta funcao devolve nulo e o botao nao aparece — que e o que o
 * pedido diz: "caso exista". O dossie, na tela dedicada, continua listando
 * todos os arquivos com os nomes reais, e e la que se resolve o caso ambiguo.
 */
function editalPublicado(arquivos: Record<string, unknown>[]): string | null {
  const achado = arquivos.find((a) => {
    if (!a.url) return false;
    const nome = `${a.titulo ?? ""} ${a.tipoDocumentoNome ?? ""}`.toLowerCase();
    return nome.includes("edital");
  });
  // `pagina` quando a fonte a manda (Sebrae): o arquivo de lá só se baixa em
  // dois passos na mesma sessão, e o endereço em `url` nem o navegador abre.
  return achado ? String(achado.pagina ?? achado.url) : null;
}

/** O documento da fonte: uma folha com a seta de baixar. */
function IconeEdital() {
  return (
    <Svg>
      <path d="M9 1.5H4.5a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1V5z" />
      <path d="M9 1.5V5h3.5" />
      <path d="M8 7.5v4" />
      <path d="m6.5 10 1.5 1.5L9.5 10" />
    </Svg>
  );
}

/** Abre numa tela inteira: uma seta saindo de uma moldura. */
function IconeTela() {
  return (
    <Svg>
      <path d="M9 2h5v5" />
      <path d="M14 2 7.5 8.5" />
      <path d="M12.5 9.5V13a1 1 0 0 1-1 1h-8a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1H7" />
    </Svg>
  );
}

/** Fecha o painel: um X. */
function IconeFechar() {
  return (
    <Svg>
      <path d="m3.5 3.5 9 9M12.5 3.5l-9 9" />
    </Svg>
  );
}

/** Fora do sistema, na origem: um globo. */
function IconeFonte() {
  return (
    <Svg>
      <circle cx="8" cy="8" r="6" />
      <path d="M2.2 6.5h11.6M2.2 9.5h11.6" />
      <path d="M8 2c1.8 2 2.6 4 2.6 6S9.8 12 8 14C6.2 12 5.4 10 5.4 8S6.2 4 8 2Z" />
    </Svg>
  );
}

/** Faz surgir: uma faisca de quatro pontas. Gira enquanto gera. */
function IconeGerar({ girando = false }: { girando?: boolean }) {
  return (
    <span className={girando ? css.girando : undefined} style={{ display: "inline-flex" }}>
      <Svg>
        <path d="M8 1.5v4M8 10.5v4M1.5 8h4M10.5 8h4" />
        <path d="m3.5 3.5 2 2M10.5 10.5l2 2M12.5 3.5l-2 2M5.5 10.5l-2 2" />
      </Svg>
    </span>
  );
}

/** Vem para voce: seta para baixo entrando numa bandeja. */
function IconeBaixar() {
  return (
    <Svg>
      <path d="M8 2v7.5" />
      <path d="m5 7 3 3 3-3" />
      <path d="M2.5 11.5V13a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1v-1.5" />
    </Svg>
  );
}

/** Vai de voce: a mesma bandeja, seta para cima. */
function IconeEnviar() {
  return (
    <Svg>
      <path d="M8 10V2.5" />
      <path d="m5 5.5 3-3 3 3" />
      <path d="M2.5 11.5V13a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1v-1.5" />
    </Svg>
  );
}

export function PainelDetalhe({
  id,
  aoFechar,
}: {
  id: string | null;
  aoFechar: () => void;
}) {
  const { data, isPending } = useQuery({
    queryKey: ["licitacao", id],
    queryFn: () => api.detalhe(id!),
    enabled: !!id,
  });
  // A aba aberta acompanha o link: a página própria abre na MESMA aba.
  const [aba] = useAba();
  const botaoFechar = useRef<HTMLButtonElement>(null);
  // Onde o painel cobre a lista (tela cheia até 980px, gaveta até 1180px), o
  // foco ficava no cartão escondido atrás dele.
  useEffect(() => {
    if (id && window.matchMedia("(max-width: 1180px)").matches) botaoFechar.current?.focus();
  }, [id]);

  if (!id) {
    return (
      <aside className={css.painel}>
        <div className={css.nada}>
          <b>Selecione uma licitação</b>
          <p>
            O detalhe completo — objeto, parecer, exigências, atestados e o porquê do
            score — aparece aqui.
          </p>
        </div>
      </aside>
    );
  }

  return (
    <aside className={`${css.painel} ${css.aberto}`} aria-label="Detalhe da licitação">
      <div className={css.rolagem}>
        <div className={css.barra}>
          <Link
            to={`/licitacao/${id}?aba=${aba}`}
            state={{ daLista: true }}
            className={css.permalink}
            title="Abre a licitação numa tela inteira, na mesma aba."
          >
            <IconeTela />
            Abrir em tela dedicada
          </Link>
          <button type="button" ref={botaoFechar} onClick={aoFechar} className={css.fechar}>
            <IconeFechar />
            Fechar
          </button>
        </div>
        {isPending || !data ? (
          <p className={css.prosa}>Carregando…</p>
        ) : (
          // `key`: cada oportunidade monta do zero — o estado de uma (a aba já
          // visitada, o feedback aberto) nunca vaza para a vizinha.
          <Corpo key={data.id} d={data} />
        )}
      </div>
    </aside>
  );
}
